import RemoveIcon from '@mui/icons-material/Remove';
import { Grid, IconButton, Paper, TextField, Typography } from '@mui/material';
import { AutomaticConditionOverlap, ConditionType } from 'interfaces/automaticCondition';
import { isEmpty, isNumber } from 'es-toolkit/compat';
import React, { FunctionComponent } from 'react';
import { AutomaticConditionBuilderProps } from './AutomaticConditionBuilder';
import AutomaticConditionGroup from './AutomaticConditionGroup';
import { isConditionOverlapParamsInvalid } from './utils';

type OverlapConditionBuilderProps = AutomaticConditionBuilderProps<AutomaticConditionOverlap>;

const OverlapConditionBuilder: FunctionComponent<React.PropsWithChildren<OverlapConditionBuilderProps>> = ({
  mappingFiltersMetadata,
  onRemove,
  editable,
  condition,
  onConditionChange,
  categoricalActions,
  orchestrationDataTypes,
}) => {
  const { filters, minFraction, maxFraction } = condition;
  const { invalidRange, invalidMinFraction, invalidMaxFraction } = isConditionOverlapParamsInvalid(condition);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string) => {
    const valueAsNumber = Number(e.target.value);

    onConditionChange({
      ...condition,
      // isEmpty test is necessary because Number('') === 0
      [field]: isEmpty(e.target.value) || isNaN(valueAsNumber) ? undefined : valueAsNumber,
    });
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1,
        mb: 1,
      }}
    >
      <Grid container alignItems={'center'}>
        <Grid container justifyContent={'space-between'}>
          <Grid item container xs={4}>
            <Grid item>
              <IconButton disabled={!editable} onClick={() => onRemove()}>
                <RemoveIcon />
              </IconButton>
            </Grid>
            <Grid item xs={4} alignContent={'center'}>
              <Typography variant="h6">Overlap</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={3} spacing={1}>
            <Grid item xs={6}>
              <TextField
                size="small"
                disabled={!editable}
                label="Min Fraction"
                type="number"
                value={isNumber(minFraction) ? Number(minFraction) : null}
                onChange={(e) => handleChange(e, 'minFraction')}
                error={invalidRange || invalidMinFraction}
                fullWidth
                inputProps={{
                  max: 1,
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                disabled={!editable}
                label="Max Fraction"
                type="number"
                value={isNumber(maxFraction) ? Number(maxFraction) : null}
                onChange={(e) => handleChange(e, 'maxFraction')}
                error={invalidRange || invalidMaxFraction}
                fullWidth
                inputProps={{
                  max: 1,
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <AutomaticConditionGroup
            editable={editable}
            onOperandsChange={(newOperands) => onConditionChange({ ...condition, filters: newOperands })}
            operands={filters}
            singleOperand={false}
            mappingFiltersMetadata={mappingFiltersMetadata}
            conditionOptions={[ConditionType.LOGICAL, ConditionType.ATTRIBUTES_TABLE]}
            categoricalActions={categoricalActions}
            orchestrationDataTypes={orchestrationDataTypes}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default OverlapConditionBuilder;
